import { create } from 'zustand';
import { PaginationType } from '../../../types';
import {ChatType, FocusMessageSafetyChoice, StoreStatus} from '../../../static';

interface ChatParticipant {
  userId: string;
  preferredFname: string;
  fname: string;
  lname: string;
  profileImageUrl: string;
}

interface ReactionInput {
  reactedBy: string;
  reaction: string;
  status?: StoreStatus | null;
}

interface GroupedReactionCount {
  reaction: string;
  count: number;
  users: ChatParticipant[];
  status?: StoreStatus | null;
}

/**
 * Chat Messages store
 */
interface useChatMessagesStore {
  messagesList: any;
  messages: any[];
  chatParticipants: ChatParticipant[];
  pagination: PaginationType;
  hasMoreMessages: boolean;
  replyBackMessage?: any;
  isLoading: boolean;
  isLoadingMoreMessages: boolean;
  initialLoading: boolean;
  selectedFocusMessage: any | null;
  focusMessageSafetyChoice: FocusMessageSafetyChoice | null;
  isFlaggingMessage: boolean;
  flagError: string | null;
  error: boolean;
  chatType?: string | undefined;
  imageFullViewOpen: boolean;
  imageFullViewData: any | null;
  openImageFullView: (message: any) => void;
  closeImageFullView: () => void;
  retryMessageId: any;
  retryAttempts: Record<string, number>;
  setState(nextState: Partial<useChatMessagesStore>): void;
  groupReactions(reactions: ReactionInput[]): GroupedReactionCount[];
  hasMessage(messageId: string): boolean;
  addReactionToMessageOptimistic(messageId: string, reaction: string, reactedBy: string, createdAt: string): void;
  addReactionToMessage(messageId: string, reaction: string, reactedBy: string, createdAt: string): void;
  confirmReactionFromBackend(messageId: string, reaction: string, reactedBy: string, createdAt: string): void;
  rollbackReaction(messageId: string, reaction: string, reactedBy: string): void;
  removeReactionFromMessage(messageId: string, reaction: string, reactedBy: string): void;
  addMessageToList(newMessage: any): void;
  addPendingMessage: (pendingMessage: Partial<any>) => void;
  replacePendingMessageWithReal: (clientMessageId: any, realMessage: any) => void;
  markMessageFailed: (clientMessageId: string) => void;
  retryMessage: (clientMessageId: string) => void;
  updateModerationByFocusMessageId: (focusMessageId: string, isMessageSafe: boolean) => void;
  updateMessageStatus(payload: {
    newStatus: 'Delivered' | 'Read';
    updates: { chatId: string; messageId: string }[];
  }): void;
}

export const initialChatMessagesState = {
  messagesList: {},
  messages: [],
  chatParticipants: [],
  replyBackMessage: null,
  pagination: {
    limit: 50,
    page: 1,
    total: 0,
    pages: 0,
  },
  hasMoreMessages: false,
  isLoading: false,
  isLoadingMoreMessages: false,
  initialLoading: false,
  selectedFocusMessage: null,
  focusMessageSafetyChoice: null,
  isFlaggingMessage: true,
  flagError: null,
  error: false,
  chatType: undefined,
  imageFullViewOpen: false,
  imageFullViewData: null,
  retryMessageId: null,
  retryAttempts: {},
};

const useChatMessages = create<useChatMessagesStore>((set, getState) => ({
  ...initialChatMessagesState,
  setState: nextState => set(state => ({ ...state, ...nextState })),
  groupReactions: function (reactions: ReactionInput[]): GroupedReactionCount[] {
    const participants = Array.isArray(getState().chatParticipants) ? getState().chatParticipants : [];
    const usersById = participants.reduce<Record<string, ChatParticipant>>((acc, user) => {
      acc[user.userId] = user;
      return acc;
    }, {});

    const grouped: Record<string, GroupedReactionCount> = {};

    for (const { reactedBy, reaction, status } of reactions.filter(
      reaction => reaction.status !== StoreStatus.DELETED
    )) {
      if (!grouped[reaction]) {
        grouped[reaction] = {
          reaction,
          count: 0,
          users: [],
        };
      }

      grouped[reaction].count++;

      if (status === StoreStatus.PENDING) {
        grouped[reaction].status = StoreStatus.PENDING;
      }

      const user = usersById[reactedBy];
      const userToAdd: ChatParticipant = user ?? {
        userId: reactedBy,
        preferredFname: 'Unknown',
        fname: 'Unknown',
        lname: '',
        profileImageUrl: '',
      };

      if (!grouped[reaction].users.some(u => u.userId === userToAdd.userId)) {
        grouped[reaction].users.push(userToAdd);
      }
    }

    return Object.values(grouped);
  },
  hasMessage: (messageId: string) => {
    return getState().messages.some((message: any) => message._id === messageId);
  },
  addReactionToMessageOptimistic: (messageId: string, reaction: string, reactedBy: string, createdAt: string) =>
    set(state => ({
      messages: state.messages.map((message: any) => {
        if (message._id !== messageId) return message;

        const reactions = message.reactions ?? [];

        const existingReaction = reactions.find((r: any) => r.reaction === reaction && r.reactedBy === reactedBy);

        if (existingReaction) {
          return {
            ...message,
            reactions: reactions.map((r: any) =>
              r.reaction === reaction && r.reactedBy === reactedBy ? { ...r, status: StoreStatus.DELETED } : r
            ),
          };
        }

        return {
          ...message,
          reactions: [
            ...reactions,
            {
              reaction,
              reactedBy,
              createdAt,
              status: StoreStatus.PENDING,
            },
          ],
        };
      }),
    })),

  addReactionToMessage: (messageId: string, reaction: string, reactedBy: string, createdAt: string) =>
    set(state => ({
      messages: state.messages.map((message: any) => {
        if (message._id !== messageId) return message;

        const reactions = message.reactions ?? [];

        const exists = reactions.some((r: any) => r.reaction === reaction && r.reactedBy === reactedBy);

        if (exists) return message;

        return {
          ...message,
          reactions: [
            ...reactions,
            {
              reaction: reaction,
              reactedBy: reactedBy,
              createdAt: createdAt,
            },
          ],
        };
      }),
    })),
  confirmReactionFromBackend: (messageId: string, reaction: string, reactedBy: string, createdAt: string) =>
    set(state => ({
      messages: state.messages.map((message: any) => {
        if (message._id !== messageId) return message;

        return {
          ...message,
          reactions: (message.reactions ?? [])
            .map((r: any) => {
              if (r.reaction === reaction && r.reactedBy === reactedBy) {
                if (r.status === StoreStatus.PENDING) {
                  const { status, ...rest } = r;
                  return {
                    ...rest,
                    createdAt,
                  };
                }

                if (r.status === StoreStatus.DELETED) {
                  return null;
                }
              }

              return r;
            })
            .filter(Boolean),
        };
      }),
    })),
  rollbackReaction: (messageId: string, reaction: string, reactedBy: string) =>
    set(state => ({
      messages: state.messages.map((message: any) => {
        if (message._id !== messageId) return message;

        return {
          ...message,
          reactions: (message.reactions ?? [])
            .map((r: any) => {
              if (r.reaction === reaction && r.reactedBy === reactedBy) {
                if (r.status === StoreStatus.PENDING) {
                  return null;
                }

                if (r.status === StoreStatus.DELETED) {
                  const { status, ...rest } = r;
                  return rest;
                }
              }

              return r;
            })
            .filter(Boolean),
        };
      }),
    })),
  removeReactionFromMessage: (messageId: string, reaction: string, reactedBy: string) =>
    set(state => ({
      messages: state.messages.map((message: any) =>
        message._id !== messageId
          ? message
          : {
              ...message,
              reactions: (message.reactions ?? []).filter(
                (r: any) => !(r.reaction === reaction && r.reactedBy === reactedBy)
              ),
            }
      ),
    })),
  openImageFullView: (message: any) =>
    set({
      imageFullViewOpen: true,
      imageFullViewData: message,
    }),

  closeImageFullView: () =>
    set({
      imageFullViewOpen: false,
      imageFullViewData: null,
    }),
  addMessageToList: (newMessage: any) =>
    set(state => {
      const currentMessages = state.messages ?? [];

      const nextMessages = [...currentMessages, newMessage];

      return {
        messages: nextMessages,
      };
    }),
  addPendingMessage: (pendingMessage: Partial<any>) =>
    set(state => ({
      messages: [...state.messages, pendingMessage],
    })),
  replacePendingMessageWithReal: (clientMessageId: any, realMessage: any) =>
    set(state => ({
      messages: state.messages.map((m: any) => (m.clientMessageId === clientMessageId ? realMessage : m)),
    })),
  markMessageFailed: (clientMessageId: string) =>
    set(state => ({
      messages: state.messages.map(m => (m.clientMessageId === clientMessageId ? { ...m, status: 'Failed' } : m)),
    })),
  retryMessage: (clientMessageId: string) =>
    set(state => {
      const currentAttempts = state.retryAttempts?.[clientMessageId] || 0;

      // Limit to 3 attempts
      if (currentAttempts >= 3) {
        return state;
      }

      return {
        retryMessageId: clientMessageId,
        messages: state.messages.map(m =>
          m.clientMessageId === clientMessageId && m.status === 'Failed' ? { ...m, status: 'pending' } : m
        ),
        retryAttempts: {
          ...state.retryAttempts,
          [clientMessageId]: currentAttempts + 1,
        },
      };
    }),
  updateModerationByFocusMessageId: (focusMessageId: string, isMessageSafe: boolean) =>
    set(state => ({
      messages: state.messages.map(item =>
        item._id === focusMessageId
          ? {
              ...item,
              moderation: {
                ...item.moderation,
                isFlaggedByAdmin: !isMessageSafe,
                isCheckedByAdmin: true,
              },
            }
          : item
      ),
    })),
  updateMessageStatus: (payload: {
    newStatus: 'Delivered' | 'Read';
    updates: { chatId: string; messageId: string }[];
  }) =>
    set(state => {
      const currentMessages = state.messages ?? [];

      if (currentMessages.length === 0) {
        return state;
      }

      if (!payload?.updates || payload.updates.length === 0) {
        return state;
      }

      const selectedChat = currentMessages[0].chatId;

      const messageIdsToUpdate = new Set(
        payload.updates.filter(update => update.chatId === selectedChat).map(update => update.messageId)
      );

      if (messageIdsToUpdate.size === 0) {
        return state;
      }

      const newMessages = currentMessages.map(m =>
        messageIdsToUpdate.has(m._id) ? { ...m, status: payload.newStatus } : m
      );

      return { messages: newMessages };
    }),
}));

export default useChatMessages;
